import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    VerticalAlign,
    HeightRule,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, generateResumePlaceholderImage, FULL_WIDTH, SCALE, createCommonHeader, createCommonFooter } from "@/utils/wordGenerator/common.js";
import { formatNumber } from "@/utils/proposalValidation.js";
import {api_baseURL} from "@/utils/init.js";

// Import Icons
import bookIcon from "@/assets/bookIcon.png";
import arrowCircleIcon from "@/assets/arrowCircleIcon.png";
import globeIcon from "@/assets/globeIcon.png";

export const createResumeTemp2 = async (resumeData, startPage) => {
    const style = resumeData.style || {};
    const sections = [];
    const projectsPerPage = resumeData.projectsPerPage || 6;

    // Fetch Icons
    const bookIconData = await fetchImageWithDimensions(bookIcon);
    const arrowIconData = await fetchImageWithDimensions(arrowCircleIcon);
    const globeIconData = await fetchImageWithDimensions(globeIcon);

    const PAGE_MARGIN = 200;
    const BODY_SPACER = 600;
    const BODY_HEIGHT = 14030;
    const CONTENT_WIDTH = FULL_WIDTH - (PAGE_MARGIN * 2);
    const SIDEBAR_WIDTH_PERCENT = 30;
    const MAIN_CONTENT_WIDTH_PERCENT = 70;

    const sidebarWidthDxa = Math.floor(CONTENT_WIDTH * (SIDEBAR_WIDTH_PERCENT / 100));
    const mainWidthDxa = Math.floor(CONTENT_WIDTH * (MAIN_CONTENT_WIDTH_PERCENT / 100));

    let currentAbsPage = startPage || 1;

    // Helper for Headings with Icons
    const createSidebarHeading = (text, iconData) => {
        const iconRun = iconData ? fitImage(iconData, 280, 280, "contain") : null;
        return new Table({
            width: { size: sidebarWidthDxa, type: WidthType.DXA },
            margins: { top: 50, bottom: 50, left: 50, right: 50 },
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.SINGLE, color: "FFFFFF", size: 12 },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [new TableRow({
                children: [
                    new TableCell({
                        width: { size: sidebarWidthDxa, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.CENTER,
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.LEFT,
                                indent: { left: 400, hanging: 400 },
                                children: [
                                    ...(iconRun ? [iconRun] : []),
                                    new TextRun({
                                        text: (iconRun ? "\t" : "") + text,
                                        bold: style.heading?.bold || true,
                                        italics: style.heading?.italic,
                                        underline: style.heading?.underline ? {} : undefined,
                                        size: (style.heading?.size || 14) * SCALE,
                                        color: fixColor(style.heading?.color || "#000000"),
                                        font: style.heading?.font
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })]
        });
    };

    // Helper for Education Block (Combined Degree & University)
    const createEducationBlock = (member) => {
        const arrowRun = arrowIconData ? fitImage(arrowIconData, 280, 280, "contain") : null;
        return new Table({
            width: { size: sidebarWidthDxa, type: WidthType.DXA },
            margins: { top: 50, bottom: 50, left: 50, right: 50 },
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [new TableRow({
                children: [
                    new TableCell({
                        width: { size: sidebarWidthDxa, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.TOP,
                        children: [
                            new Paragraph({
                                indent: { left: 400, hanging: 400 },
                                spacing: { before: 100 },
                                children: [
                                    ...(arrowRun ? [arrowRun] : []),
                                    new TextRun({
                                        text: (arrowRun ? "\t" : "") + (member.degree || "N/A"),
                                        bold: style.label?.bold || true,
                                        italics: style.label?.italic,
                                        underline: style.label?.underline ? {} : undefined,
                                        size: (style.label?.size || 11) * SCALE,
                                        color: fixColor(style.label?.color || "#000000"),
                                        font: style.label?.font
                                    })
                                ]
                            }),
                            new Paragraph({
                                spacing: { after: 200 },
                                indent: { left: 400 }, // Align university with degree text
                                children: [new TextRun({
                                    text: member.university || "N/A",
                                    size: (style.text?.size || 10) * SCALE,
                                    color: fixColor(style.text?.color || "#333333"),
                                    font: style.text?.font,
                                    bold: style.text?.bold,
                                    italics: style.text?.italic,
                                    underline: style.text?.underline ? {} : undefined,
                                })]
                            })
                        ]
                    })
                ]
            })]
        });
    };

    // Helper for List Items (Options for Bullets)
    const createListItem = (text, itemStyle, spacingAfter = 0, showBullet = true) => {
        return new Table({
            width: { size: sidebarWidthDxa, type: WidthType.DXA },
            margins: { top: 50, bottom: 50, left: 50, right: 50 },
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [new TableRow({
                children: [
                    new TableCell({
                        width: { size: sidebarWidthDxa, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.TOP,
                        children: [
                            new Paragraph({
                                spacing: { before: 100, after: spacingAfter },
                                indent: showBullet ? { left: 400, hanging: 400 } : undefined,
                                children: [
                                    new TextRun({
                                        text: (showBullet ? "•\t" : "") + text,
                                        ...itemStyle
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })]
        });
    };

    for (const member of resumeData.members) {
        const memberProjects = member.projects || [];
        const numPages = Math.max(1, Math.ceil(memberProjects.length / projectsPerPage));

        for (let p = 0; p < numPages; p++) {
            const isFirst = p === 0;
            const pageProjects = memberProjects.slice(p * projectsPerPage, (p + 1) * projectsPerPage);

            // ================= SIDEBAR (LEFT) =================
            const sidebarChildren = [];

            if (isFirst) {
                // 1. Profile Picture
                let profileImgRun = null;
                const imgUrl = `${api_baseURL}profilePicture?tmid=${member.teamMemberId}`;
                const profileImgData = member.profilePicturePath ?
                    await fetchImageWithDimensions(imgUrl) :
                    generateResumePlaceholderImage(sidebarWidthDxa, 3000, fixColor(style.colorScheme?.body || "EEEEEE"));

                if (profileImgData) {
                    profileImgRun = fitImage(profileImgData, sidebarWidthDxa, 3000, "cover");
                } else {
                    profileImgRun = fitImage(generateResumePlaceholderImage(sidebarWidthDxa, 3000, fixColor(style.colorScheme?.body || "EEEEEE")), sidebarWidthDxa, 3000, "cover");
                }

                sidebarChildren.push(new Table({
                    width: { size: sidebarWidthDxa, type: WidthType.DXA },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [new TableRow({
                        height: { value: 3000, rule: HeightRule.EXACT },
                        children: [new TableCell({
                            shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                            children: [new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: profileImgRun ? [profileImgRun] : []
                            })]
                        })]
                    })]
                }));

                // 2. Name & Role Box
                sidebarChildren.push(new Table({
                    width: { size: sidebarWidthDxa, type: WidthType.DXA },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [new TableRow({
                        children: [new TableCell({
                            shading: { fill: "E66E2D" },
                            margins: { top: 100, bottom: 100, left: 100, right: 100 },
                            children: [
                                new Paragraph({
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: (member.teamMemberName || "N/A").toUpperCase(),
                                            color: "FFFFFF",
                                            bold: true,
                                            size: 18 * SCALE,
                                            font: "sans-serif"
                                        }),
                                        new TextRun({ break: 1 }),
                                        new TextRun({
                                            text: (member.role || "N/A").toUpperCase(),
                                            color: "FFFFFF",
                                            size: 18 * SCALE,
                                            font: "sans-serif"
                                        })
                                    ]
                                })
                            ]
                        })]
                    })]
                }));

                // 3. Education
                sidebarChildren.push(createSidebarHeading("EDUCATION", bookIconData));
                if (member.education && member.education.length > 0) {
                    member.education.forEach((edu, index) => {
                        sidebarChildren.push(createEducationBlock(edu));
                    });
                } else {
                    sidebarChildren.push(createEducationBlock({ degree: "N/A", university: "N/A" }));
                }

                // 4. Affiliations & Certificates
                sidebarChildren.push(createSidebarHeading("AFFILIATIONS & CERTIFICATES", globeIconData));
                if (member.certifications && member.certifications.length > 0) {
                    member.certifications.forEach((cert, index) => {
                        const isLast = index === member.certifications.length - 1;
                        sidebarChildren.push(createListItem(cert, {
                            size: (style.text?.size || 10) * SCALE,
                            color: fixColor(style.text?.color || "#333333"),
                            font: style.text?.font,
                            bold: style.text?.bold,
                            italics: style.text?.italic,
                            underline: style.text?.underline ? {} : undefined,
                        }, isLast ? 200 : 0, true));
                    });
                } else {
                    sidebarChildren.push(createListItem("N/A", {
                        size: (style.text?.size || 10) * SCALE,
                        color: fixColor(style.text?.color || "#333333"),
                        font: style.text?.font
                    }, 200, true));
                }

                const sidebarTopContent = [...sidebarChildren];
                sidebarChildren.length = 0;

                // 4. Experience Box
                const experienceBox = new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [new TableRow({
                        children: [new TableCell({
                            shading: { fill: "373236" },
                            margins: { top: 100, bottom: 100 },
                            children: [
                                new Paragraph({
                                    alignment: AlignmentType.CENTER,
                                    children: [new TextRun({
                                        text: `${member.yearsOfExperience || 0} YEARS EXPERIENCE`,
                                        color: "FFFFFF",
                                        bold: true,
                                        size: 12 * SCALE,
                                        font: "sans-serif"
                                    })]
                                })
                            ]
                        })]
                    })]
                });

                sidebarChildren.push(new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [
                        new TableRow({
                            height: { value: Math.floor(BODY_HEIGHT * 0.9), rule: HeightRule.EXACT },
                            children: [new TableCell({
                                verticalAlign: VerticalAlign.TOP,
                                children: sidebarTopContent
                            })]
                        }),
                        new TableRow({
                            height: { value: Math.floor(BODY_HEIGHT * 0.1), rule: HeightRule.EXACT },
                            children: [new TableCell({
                                verticalAlign: VerticalAlign.BOTTOM,
                                children: [experienceBox]
                            })]
                        })
                    ]
                }));
            } else {
                sidebarChildren.push(new Paragraph({ children: [] }));
            }

            // ================= MAIN CONTENT (RIGHT) =================
            const experienceChildren = [];
            for (const proj of pageProjects) {
                let projImgRun = null;
                const targetPhoto = proj.uploadedPhoto || proj.photoURL;
                if (targetPhoto) {
                    const imgData = await fetchImageWithDimensions(targetPhoto);
                    if (imgData) {
                        projImgRun = fitImage(imgData, 2000, 1600, "cover");
                    }
                }

                experienceChildren.push(new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [new TableRow({
                        children: [
                            new TableCell({
                                margins: { top: 100, bottom: 100 },
                                width: { size: 2000, type: WidthType.DXA },
                                children: [new Table({
                                    width: { size: 2000, type: WidthType.DXA },
                                    borders: {
                                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                                    },
                                    rows: [new TableRow({
                                        height: { value: 1600, rule: HeightRule.EXACT },
                                        children: [new TableCell({
                                            shading: projImgRun ? undefined : { fill: "FFFFFF" },
                                            children: [new Paragraph({
                                                alignment: AlignmentType.CENTER,
                                                children: projImgRun ? [projImgRun] : []
                                            })]
                                        })]
                                    })]
                                })]
                            }),
                            new TableCell({ width: { size: 200, type: WidthType.DXA }, children: [] }),
                            new TableCell({
                                width: { size: mainWidthDxa - 2200, type: WidthType.DXA },
                                children: [
                                    new Paragraph({
                                        spacing: { after: 10 },
                                        children: [
                                            new TextRun({
                                                text: (proj.projectName || "N/A") + " ",
                                                bold: style.label?.bold || true,
                                                italics: style.label?.italic,
                                                underline: style.label?.underline ? {} : undefined,
                                                size: (style.label?.size || 11) * SCALE,
                                                color: fixColor(style.label?.color || "#000000"),
                                                font: style.label?.font
                                            }),
                                            new TextRun({
                                                text: `Size: ${proj.projectSize ? formatNumber(proj.projectSize) + " SF" : "N/A"} | `,
                                                size: (style.text?.size || 10) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            }),
                                            new TextRun({
                                                text: `Value: ${proj.value ? "$" + formatNumber(proj.value) : "N/A"} | `,
                                                size: (style.text?.size || 10) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            }),
                                            new TextRun({
                                                text: `Owner: ${proj.client || "N/A"} | `,
                                                size: (style.text?.size || 10) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            }),
                                            new TextRun({
                                                text: `Completion Date: ${proj.constructionCompletionDate ? new Date(proj.constructionCompletionDate).getFullYear() : "N/A"} | `,
                                                size: (style.text?.size || 10) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            }),
                                            new TextRun({
                                                text: `Scope: ${proj.projectDetails || "N/A"}`,
                                                size: (style.text?.size || 10) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })]
                }));
                experienceChildren.push(new Paragraph({ spacing: { after: 20 } }));
            }

            // ================= ASSEMBLE PAGE CONTENT =================
            const bodyContentTable = new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: [new TableRow({
                    height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                    children: [
                        new TableCell({
                            width: { size: sidebarWidthDxa, type: WidthType.DXA },
                            shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                            children: sidebarChildren
                        }),
                        new TableCell({ width: { size: 400, type: WidthType.DXA }, children: [] }),
                        new TableCell({
                            margins: { left: 100, right: 100 },
                            width: { size: mainWidthDxa - 400, type: WidthType.DXA },
                            shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                            children: experienceChildren
                        })
                    ]
                })]
            });

            // ================= PAGE STRUCTURE =================
            const headerTable = createCommonHeader(resumeData.title, fixColor(style.colorScheme?.header || "#545454"));
            const footerTable = createCommonFooter(currentAbsPage, fixColor(style.colorScheme?.footer || "#545454"));

            const mainTable = new Table({
                width: { size: FULL_WIDTH, type: WidthType.DXA },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: [
                    new TableRow({ children: [new TableCell({ children: [headerTable] })] }),
                    new TableRow({ height: { value: BODY_SPACER, rule: HeightRule.EXACT }, children: [new TableCell({ children: [] })] }),
                    new TableRow({
                        height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                        children: [new TableCell({
                            children: [new Table({
                                width: { size: FULL_WIDTH, type: WidthType.DXA },
                                borders: {
                                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                                },
                                rows: [new TableRow({
                                    height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                                    children: [
                                        new TableCell({ width: { size: PAGE_MARGIN, type: WidthType.DXA }, children: [] }),
                                        new TableCell({ width: { size: CONTENT_WIDTH, type: WidthType.DXA }, children: [bodyContentTable] }),
                                        new TableCell({ width: { size: PAGE_MARGIN, type: WidthType.DXA }, children: [] }),
                                    ]
                                })]
                            })]
                        })]
                    }),
                    new TableRow({ height: { value: BODY_SPACER, rule: HeightRule.AT_LEAST }, children: [new TableCell({ children: [] })] }),
                    new TableRow({ children: [new TableCell({ children: [footerTable] })] })
                ]
            });

            sections.push({
                properties: { page: { margin: { top: 0, right: 0, bottom: 0, left: 0 } } },
                children: [mainTable]
            });
            currentAbsPage++;
        }
    }

    return sections;
};
